import React from 'react';
import type { AnalyticsConfig } from '@jira/growth__experiments-ad-controls/src/button/types.tsx';
import type { ReasonsModalConfig } from '@jira/growth__experiments-ad-controls/src/dropdown/types.tsx';
import { defineMessage } from '@atlassian/jira-intl';
import { AdControlsButton } from '@jira/growth__experiments-ad-controls/src/button/AdControlsButton.tsx';
import { useRoadmapUpsellAdControls } from './useRoadmapUpsellAdControls.tsx';

const dismissFlagTitle = defineMessage({
	id: 'jira-roadmap-in-nav.ad-controls.dismiss-flag.title',
	defaultMessage: 'Dismiss this recommendation',
	description: 'Title for the dismiss flag in the ad controls menu',
});

const dismissFlagDescription = defineMessage({
	id: 'jira-roadmap-in-nav.ad-controls.dismiss-flag.description',
	defaultMessage: 'This will remove this recommendation from your view',
	description: 'Description for the dismiss flag in the ad controls menu',
});

const reasonsModalTitle = defineMessage({
	id: 'jira-roadmap-in-nav.ad-controls.reasons-modal.title',
	defaultMessage: 'Why am I seeing this?',
	description: 'Title for the reasons modal in the ad controls menu',
});

const reasonsModalExplanation = defineMessage({
	id: 'jira-roadmap-in-nav.ad-controls.reasons-modal.explanation',
	defaultMessage:
		'We think <strong>Roadmaps</strong> will help your product teams connect high-level strategy directly to Jira work items. This recommendation is based on:',
	description: 'Explanation text for the reasons modal',
});

const reasonsModalBulletOne = defineMessage({
	id: 'jira-roadmap-in-nav.ad-controls.reasons-modal.bullet-one',
	defaultMessage:
		'<strong>Your Jira Premium plan.</strong> Roadmaps is a Jira Product Discovery Premium feature designed to bridge the gap between discovery and delivery.',
	description: 'First bullet point in the reasons modal',
});

const reasonsModalBulletTwo = defineMessage({
	id: 'jira-roadmap-in-nav.ad-controls.reasons-modal.bullet-two',
	defaultMessage:
		'<strong>Your role as an Admin.</strong> We want to provide best practices on how to manage roadmaps for an entire portfolio.',
	description: 'Second bullet point in the reasons modal',
});

export const RoadmapUpsellAdControlsButton: React.FC = () => {
	const { dismiss } = useRoadmapUpsellAdControls();

	const analyticsConfig: AnalyticsConfig = {
		errorBoundary: {
			id: 'jpdRoadmapNavButton',
			context: 'Failed rendering RoadmapsUpsell ad controls button',
			packageName: 'jiraRoadmapNav',
			teamName: 'JPD - Juno',
		},
		screen: {
			sourceName: 'roadmapsUpsellAdControls',
			name: 'roadmapsUpsellAdControls',
			messageId: 'roadmaps-upsell.adcontrols.dropdown',
		},
		reasonsModal: {
			screenName: 'roadmaps-upsell.ad-controls.reasons-modal',
			linkAttributes: {},
		},
	};

	const reasonsModalConfig: ReasonsModalConfig = {
		explanation: reasonsModalExplanation,
		reasons: [
			{
				id: 'jira-premium-plan',
				message: reasonsModalBulletOne,
			},
			{
				id: 'admin-role',
				message: reasonsModalBulletTwo,
			},
		],
		dismissFlag: {
			title: dismissFlagTitle,
			description: dismissFlagDescription,
		},
		modalTitle: reasonsModalTitle,
	};

	return (
		<AdControlsButton
			dismiss={dismiss}
			itemId="roadmaps-upsell-ad-controls"
			analytics={analyticsConfig}
			productName="Jira"
			reasonsModalConfig={reasonsModalConfig}
		/>
	);
};
